import React, { useState } from 'react'
import Autosuggest from 'react-autosuggest'
import TextField from '@material-ui/core/TextField'
import Paper from '@material-ui/core/Paper'
import MenuItem from '@material-ui/core/MenuItem'
import Popper from '@material-ui/core/Popper'
import { Field, change } from 'redux-form'
import { makeStyles } from '@material-ui/core/styles'
import { fetchPatientAutoComplete } from '../../../redux/actions'
import { useSelector, shallowEqual } from 'react-redux'
import { patientAutoCompleteSuggestionSelector } from '../../../redux/selectors'
import { useConstant } from '../../../utilities/useConstant'
import { debounceMilliseconds, minimumSearchCharacters } from '../../../utilities/constants'

var debounce = require('lodash/debounce')

const useASStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1
	},
	container: {
		position: 'relative'
	},
	suggestionsContainerOpen: {
		position: 'absolute',
		zIndex: 1,
		marginTop: theme.spacing(1),
		left: 0,
		right: 0
	},
	suggestion: {
		display: 'block'
	},
	suggestionsList: {
		margin: 0,
		padding: 0,
		listStyleType: 'none'
	},
	optionStyle: {
		overflowY: 'auto',
		'WebkitOverflowScrolling': 'touch',
		'& ul li .MuiMenuItem-root': {
			whiteSpace: 'normal',
		}
	},
	divider: {
		height: theme.spacing(2)
	}
}))

const styles = {
	input: {
		color: '#fff',
		fontSize: '1rem',
		backgroundColor: 'rgb(36, 48, 65)',
	},

	root: {
		color: '#fff'
	},
	focused: {
		color: '#fff',
		backgroundColor: 'rgb(36, 48, 65)',
		'&$focused': {
			color: '#fff',
			backgroundColor: 'rgb(36, 48, 65)'
		}
	}
}

const useLabelStyles = makeStyles({
	root: {
		color: '#fff',
		marginLeft: '-2px'
	}
})

const useInputStyles = makeStyles({
	root: {
		color: '#fff',
		backgroundColor: 'rgb(36, 48, 65)',
		marginBottom: '25px'
	},
	focused: {
		color: '#fff',
		backgroundColor: 'rgb(36, 48, 65)',
		'&$focused': {
			color: '#fff',
			backgroundColor: 'rgb(36, 48, 65)'
		}
	}
})

export const PatientSelectorControl = ({
	label,
	meta: { initial, dispatch, form, touched, error, invalid },
	input,
	noMatchText,
	onPatientSelected
}) => {
	const patients = useSelector(patientAutoCompleteSuggestionSelector, shallowEqual)
	var labelStyles = useLabelStyles()
	var inputStyles = useInputStyles()
	const [ noMatch, setNoMatch ] = useState(false)
	const [ matchSelected, setMatchSelected ] = useState(false)
	const [ stateSuggestions, setSuggestions ] = useState(patients)
	const classes = useASStyles()
	const [ anchorEl, setAnchorEl ] = React.useState(null)
	const [ state, setState ] = React.useState({
		label: ''
	})

	const handleSuggestionsFetchRequested = ({ value }) => {
		if (value && value.length > minimumSearchCharacters) {
			dispatch(fetchPatientAutoComplete(value, noMatchText))
		}
	}

	const onSuggestionRequested = useConstant(() =>
		debounce(handleSuggestionsFetchRequested, debounceMilliseconds, { leading: true })
	)

	function renderSuggestion(suggestion, { query, isHighlighted }) {
		return (
			<MenuItem data-testid="patient-selector-menu-item" selected={isHighlighted} component='div'>
				<div>
					{suggestion.id36} 
					{!suggestion.email ? '' : ' | ' + (suggestion.email.length > 50 ? (
						suggestion.email.substring(0, 49) + '...'
					) : (
						suggestion.email
					))}
				</div>
			</MenuItem>
		)
	}

	function renderInputComponent(inputProps) {
		const { classes, inputRef = () => {}, ref, ...other } = inputProps

		return (
			<TextField
				data-testid="patient-selector"
				fullWidth
				InputLabelProps={{
					classes: {
						root: labelStyles.root,
						focused: labelStyles.root,
						shrink: labelStyles.root
					}
				}}
				error={touched && invalid}
				helperText={touched && error}
				classes={{
					root: inputStyles.root,
					error: inputStyles.error
				}}
				style={styles}
				InputProps={{
					...inputProps,
					inputRef: (node) => {
						ref(node)
						inputRef(node)
					}
				}}
				inputProps={{ autocomplete: 'dont' }}
				shrink='true'
				variant='filled'
				{...other}
			/>
		)
	}

	const handleSuggestionsClearRequested = () => {
		setSuggestions([])
	}

	const onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) => {
		if (suggestion && suggestion.id > -1) {
			dispatch(change(form, input.name, suggestion.id))
			setNoMatch(false)
			setMatchSelected(true)
		} else {
			setState({
				...state,
				value: null,
				label: ''
			})
			dispatch(change(form, input.name, ''))
		}
		
		if(onPatientSelected) {
			onPatientSelected(suggestion)
		}
	}

	function getSuggestionValue(suggestion) {
		return suggestion
	}

	const autosuggestProps = {
		renderInputComponent,
		suggestions: patients,
		onSuggestionsFetchRequested: onSuggestionRequested,
		onSuggestionsClearRequested: handleSuggestionsClearRequested,
		onSuggestionSelected: onSuggestionSelected,
		getSuggestionValue,
		renderSuggestion
	}

	const handleChange = (event, { newValue }) => {
		if (newValue && newValue.id && newValue.id > 0) {
			dispatch(change(form, input.name, newValue.id))
			setNoMatch(false)
			setMatchSelected(true)
		} else {
			dispatch(change(form, input.name, null))
			setNoMatch(true)
			setMatchSelected(false)
		}
		setState({
			...state,
			value: newValue,
			label: newValue && newValue.email ? newValue.email : newValue.id36 ? newValue.id36 : newValue
		})
	}

	return (
		<div className={classes.root}>
			<Autosuggest
				data-testid="patient-selector-autosuggest"
				{...autosuggestProps}
				inputProps={{
					classes,
					id: input.name,
					label: label,
					value: state.label,
					onChange: handleChange,
					onBlur: (e, x) => {
						setAnchorEl(null)

						//input.onChange(e.target.value)
						if (noMatch || !matchSelected) {
							setState({
								...state,
								value: '',
								label: ''
							})
							dispatch(change(form, input.name, ''))
						}
					},
					inputRef: (node) => {
						setAnchorEl(node)
					}
				}}
				theme={{
					suggestionsList: classes.suggestionsList,
					suggestion: classes.suggestion
				}}
				renderSuggestionsContainer={(options) => (
					<Popper data-testid="patient-selector-popper" anchorEl={anchorEl} open={Boolean(options.children)} style={{ zIndex: 1300 }}
                		placement="bottom-start"
						modifiers={{
      						flip: {
        						enabled: false,
      						},
	  						preventOverflow: {
      						  	enabled: true,
      						  	boundariesElement: 'viewPort',
      						},
						}}
					>
						<Paper
							data-testid="patient-selector-paper"
							square
							{...options.containerProps}
							className={classes.optionStyle}
							style={{
								maxHeight: anchorEl ? window.innerHeight-anchorEl.parentElement.getBoundingClientRect().bottom : undefined,
								width: anchorEl ? anchorEl.parentElement.clientWidth : undefined,
								marginLeft: anchorEl ? -(anchorEl.parentElement.clientWidth-anchorEl.clientWidth) : undefined }}>
							{options.children}
						</Paper>
					</Popper>
				)}
			/>
		</div>
	)
}

export default ({ label, name, validators, maxLength = 255, noMatchText = 'No Entries Found', ...other }) => {
	return (
		<Field
			data-testid="patient-selector-field"
			name={name}
			component={PatientSelectorControl}
			label={label}
			maxLength={maxLength}
			fullWidth
			validate={validators}
			noMatchText={noMatchText}
			{...other}
		/>
	)
}
